import React, { useEffect, useRef } from 'react'

import { dataIdFromObject } from '@sketch/graphql-cache'

import {
  LoadingPlaceholder,
  Flex,
  AspectRatioGrid,
  InfiniteList,
  handleFetchMore,
  LoadingState,
} from '@sketch/components'

import EmptyState from '../../../components/EmptyStateOld'
import LoadingError from '../../components/Layout/LoadingError'
import { Layout } from '../../components/Layout'
import { Group, GroupName, ListGridSeparator } from '../../components/Grid'
import { LayerStyleListItem } from './LayerStyleListItem'

import useScrollReset from '../../hooks/useScrollReset'
import { useGetComponentsCount, useGetLayerStyles } from '../../operations'
import { useGetPendingDescriptionQuery } from '@sketch/gql-types'
import { GridSize, useCustomGridContext } from '../../context/CustomGridContext'
import { useSearchComponentsContext } from '../../context/SearchComponentsContext'
import { useSelectedGroupContext } from '../../context/SelectedGroupContext'
import { useTrackTimeInComponentPage, useComponentLink } from '../../hooks'
import { useFlag, useAnalytics, getRenderStatus } from '@sketch/modules-common'

// This is a valid use case to validate TextStyle
// eslint-disable-next-line no-restricted-imports
import { LayerStyle } from '@sketch/gql-types/expansive'

import { groupStyles } from '../../utils'
import { Center } from '../../components/Center'
import ComponentsEmptyState from '../../../components/ComponentsEmptyState'

const LayerList = ({
  searchValue,
  path,
}: {
  searchValue: string
  path: string | null
}) => {
  useScrollReset()

  const isComponentDescriptionsOn = useFlag('components-description')

  const { trackEvent } = useAnalytics()

  const prevSearchRef = useRef<string | undefined>()

  const {
    defaultColumns,
    verticalRowSpaceGrid,
    minColumnWidth,
    setSelectedGridSize,
    selectedGridSize,
  } = useCustomGridContext()

  if (selectedGridSize === GridSize.Full) {
    setSelectedGridSize(GridSize.Medium)
  }

  const {
    shareIdentifier,
    versionIdentifier,
    documentVersion,
    compatibilityVersion,
    canEditDescriptions,
    renderStatusOfLatestVersion,
    entries,
    after,
    fetchMore,
    loading,
    error,
    hasComponentManifest,
    componentsState,
  } = useGetLayerStyles({
    search: searchValue,
    path: path === 'View All' ? null : path,
  })

  const { data: pendingDescriptionsData, loading: pendingDescriptionsLoading } =
    useGetPendingDescriptionQuery({
      variables: {
        shareIdentifier,
        versionIdentifier: versionIdentifier || '',
      },
      skip:
        loading ||
        !versionIdentifier ||
        getRenderStatus(renderStatusOfLatestVersion) === 'ready' ||
        !isComponentDescriptionsOn,
    })

  // Allows us to link to an specific component (automatically selecting it and
  // showing the inspector data)
  useComponentLink(loading)

  useEffect(() => {
    if (searchValue) {
      trackEvent('CWV - search', { type: 'layer styles' })
    }
  }, [searchValue, trackEvent])

  useEffect(() => {
    // only update the searchValue after it has finished loading
    if (!loading) {
      prevSearchRef.current = searchValue
    }
  }, [searchValue, loading])

  if (error) {
    return <LoadingError error={error} componentType="Layer Styles" />
  }

  const switchingGroupToSearch =
    (path !== 'View All' || path === null) && searchValue

  const isLoadingSearch =
    loading &&
    searchValue &&
    (prevSearchRef.current === undefined ||
      prevSearchRef.current !== searchValue)

  if (
    (loading && !after) ||
    switchingGroupToSearch ||
    isLoadingSearch ||
    pendingDescriptionsLoading
  ) {
    return (
      <Flex justifyContent="center" flex="auto">
        <LoadingPlaceholder size="64px" />
      </Flex>
    )
  }

  if (!hasComponentManifest || componentsState === 'CANNOT_PROCESS') {
    return (
      <EmptyState
        title="Components aren’t available for this version"
        description=""
      />
    )
  }

  const layerGroups = groupStyles(entries)
  const groupsNames = Object.keys(layerGroups)

  if (groupsNames.length === 0) {
    if (searchValue !== '') {
      return (
        <EmptyState
          title={`No search results for "${searchValue}"`}
          description="No search results."
          icon="search"
        />
      )
    }

    if (path !== '' && path !== 'View All') {
      return (
        <EmptyState
          title={`The group "${path}" does not exist in this version.`}
          description=""
          icon="layerStyle"
        />
      )
    }

    return (
      <EmptyState
        title="No Layer Styles"
        description={
          <>
            This Document does not contain any{' '}
            <a href="https://www.sketch.com/docs/designing/styling/layer-styles/">
              Layer Styles
            </a>
            .
          </>
        }
        icon="layerStyle"
      />
    )
  }

  const generateLayerStyleList = (
    layerStyle: LayerStyle,
    groupName: string
  ) => {
    const editingComponent = (
      pendingDescriptionsData?.collaborativeEditingSession.componentEdits || []
    ).find(
      component =>
        (component.componentUuid as string).toUpperCase() === layerStyle.uuid
    )

    const pendingDescription = {
      documentVersion: documentVersion!,
      baseVersionIdentifier: versionIdentifier,
      compatibilityVersion: compatibilityVersion!,
      canEditDescriptions,
      shareIdentifier,
      componentUuid: layerStyle.uuid,
      description: editingComponent?.description || layerStyle.description,
    }

    return (
      <LayerStyleListItem
        key={`${layerStyle.identifier}-${versionIdentifier}`}
        layerStyle={layerStyle}
        groupName={groupName}
        searchValue={searchValue}
        pendingDescription={pendingDescription}
      />
    )
  }

  // The main difference between the search view and the normal view is that we
  // don't group items when searching, that's why we need to tweak the list generation
  if (searchValue !== '') {
    return (
      <InfiniteList
        canLoadMore={after !== null}
        onLoadMore={handleFetchMore(
          fetchMore,
          ['share', 'version', 'document', 'components', 'entries'],
          { dataIdFromObject, after: after || null }
        )}
      >
        <AspectRatioGrid
          verticalRowSpace={verticalRowSpaceGrid}
          columns={defaultColumns}
          gutterSize={32}
          minWidth={minColumnWidth}
        >
          {groupsNames.map(groupName =>
            layerGroups[groupName].map((layerStyle: LayerStyle) =>
              generateLayerStyleList(layerStyle, groupName)
            )
          )}
        </AspectRatioGrid>
      </InfiniteList>
    )
  }

  return (
    <InfiniteList
      canLoadMore={after !== null}
      onLoadMore={handleFetchMore(
        fetchMore,
        ['share', 'version', 'document', 'components', 'entries'],
        { dataIdFromObject, after: after }
      )}
    >
      {groupsNames.map((groupName, key) => (
        <Group key={`${groupName}-${versionIdentifier}`}>
          {key !== 0 && <ListGridSeparator />}
          {groupName !== ' ' && <GroupName groups={groupName} />}
          <AspectRatioGrid
            verticalRowSpace={verticalRowSpaceGrid}
            columns={defaultColumns}
            gutterSize={32}
            minWidth={minColumnWidth}
          >
            {layerGroups[groupName].map((layerStyle: LayerStyle) =>
              generateLayerStyleList(layerStyle, groupName)
            )}
          </AspectRatioGrid>
        </Group>
      ))}
    </InfiniteList>
  )
}

interface LayerStylesProps {
  userIsEditor: boolean
}

export const LayerStyles: React.FC<LayerStylesProps> = ({ userIsEditor }) => {
  const { totalComponents, loading, componentsState } = useGetComponentsCount()
  const { search } = useSearchComponentsContext()
  const { selected, setSelected } = useSelectedGroupContext()

  useTrackTimeInComponentPage('layer-styles')

  if (search) {
    setSelected('View All')
  }

  if (
    componentsState === 'CAN_PROCESS' ||
    componentsState === 'PROCESSING' ||
    loading ||
    totalComponents === false
  ) {
    return (
      <Center>
        <div>
          <LoadingState />
          <p>Loading components…</p>
        </div>
      </Center>
    )
  }

  if (totalComponents === 0) {
    return <ComponentsEmptyState userIsEditor={userIsEditor} />
  }

  return (
    <Layout>
      <LayerList searchValue={search} path={selected} />
    </Layout>
  )
}
